// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  TagsByObjectKey,
  TagsByObjectEntry,
} from '@shared/data-access/interfaces/redux/tags-management-state-types';
import { type AdvisoryInstitution } from '@shared/data-access/types/brokerage-types';
import { useStateValue } from '@shared/ui-kit';
import { useCallback, useMemo } from 'react';

export const useAccounts = (
  data: AdvisoryInstitution[],
  institutionId: string,
  clientId?: number,
  selectedTag?: string,
  tagsByObject?: Map<TagsByObjectKey, TagsByObjectEntry>,
) => {
  const [accountsSelected, setSelectedAccounts] = useStateValue<Set<string>>(
    `aggregator-accounts-selected-${institutionId}-${clientId}`,
    () => new Set(),
  );

  const accountOptions = useMemo(
    () =>
      data.flatMap(institution =>
        institution.products.accounts.map(account => ({
          iban: account.iban,
          name: account.name ?? institution.name,
          institution_id: institution.id,
          institution_name: institution.name,
          cash: account.cash ?? 0,
        })),
      ),
    [data],
  );

  const validIbans = useMemo(
    () => new Set(accountOptions.map(a => a.iban)),
    [accountOptions],
  );

  const selectedAccounts = useMemo(() => {
    if (data.length > 0 && accountsSelected.size === 0)
      return accountOptions.map(account => account.iban);
    return Array.from(accountsSelected).filter(iban => validIbans.has(iban));
  }, [accountOptions, accountsSelected, data.length, validIbans]);

  const onChangeAccounts = useCallback(
    (accounts: string[]) => {
      setSelectedAccounts(new Set(accounts));
    },
    [setSelectedAccounts],
  );

  return {
    accountOptions,
    selectedAccounts,
    onChangeAccounts,
  } as const;
};
